import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useState, useCallback, useContext } from "react";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import API_URLS from "../../../constants/apiConstants";
import { useNavigate } from "react-router-dom";
import { BiX } from "react-icons/bi";
import "../css/RoomModal.css";
import AuthContext from "../../../context/AuthProvider";
import { useTranslation } from "react-i18next";

export default function AddModal({ open, onClose, title }) {
  const axiosPrivate = useAxiosPrivate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { school } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const type = title?.toLowerCase();
  const isStaff = type === "staff";

  // Staff starts with an email check (same flow as ParentModal):
  // "email" -> "existing" | "form".  Other types go straight to the form.
  const initialStep = isStaff ? "email" : "form";
  const [step, setStep] = useState(initialStep);
  const [existingStaff, setExistingStaff] = useState(null);
  const [checkStatus, setCheckStatus] = useState(null);

  const resetForm = useCallback(() => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setErrors({});
    setStep(initialStep);
    setExistingStaff(null);
    setCheckStatus(null);
  }, [initialStep]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  /* ---------------- Step 1: check email (staff only) ---------------- */
  const handleCheckEmail = async () => {
    const value = email.trim();
    if (!value) {
      setErrors({ email: t("staff_modal.errors.emailRequired") });
      return;
    }
    if (!/\S+@\S+\.\S+/.test(value)) {
      setErrors({ email: t("staff_modal.errors.emailInvalid") });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const res = await axiosPrivate.get(
        API_URLS.checkNurseryStaffEmail(school.id, value)
      );
      const status = res.data?.status;

      if (status === "not_found") {
        setStep("form");
      } else if (status === "found" || status === "already_in_this_nursery") {
        setExistingStaff(res.data.staff);
        setCheckStatus(status);
        setStep("existing");
      } else if (status === "invalid_type") {
        setErrors({ email: t("staff_modal.errors.invalidType") });
      } else {
        setErrors({ server: t("form.genericError") });
      }
    } catch (error) {
      setErrors({
        server:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          t("form.genericError"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  /* -------- Step 2 (existing): link staff to this nursery -------- */
  const handleUseExistingStaff = async () => {
    if (!existingStaff?.id) return;

    setIsLoading(true);
    try {
      // Staff from another nursery must be linked first;
      // one already in this nursery just opens its details.
      if (checkStatus === "found") {
        await axiosPrivate.post(API_URLS.linkStaffToNursery(school.id), {
          staff_id: existingStaff.id,
          status: "active",
        });
      }
      const id = existingStaff.id;
      handleClose();
      navigate(`details/${id}`);
    } catch (error) {
      setErrors({
        server:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          t("form.genericError"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- Step 3: create new (form) ---------------- */
  const validateForm = () => {
    const newErrors = {};
    if (!firstName.trim()) {
      newErrors.firstName = t("staff_modal.errors.firstNameRequired");
    }
    if (!lastName.trim()) {
      newErrors.lastName = t("staff_modal.errors.lastNameRequired");
    }
    if (isStaff) {
      if (!email.trim()) {
        newErrors.email = t("staff_modal.errors.emailRequired");
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        newErrors.email = t("staff_modal.errors.emailInvalid");
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const formData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      };

      if (type === "student") {
        formData.nurseries = [school.id];
      } else if (isStaff) {
        formData.email = email.trim();
        formData.status = "active";
      } else {
        formData.Nursery_id = school.id;
      }
      // NOTE: this fallback only runs for a `title` other than "student"/"staff"
      // (e.g. "parent"/"room"). If this modal is reused for another entity,
      // wire it to that entity's real create endpoint instead
      // (e.g. API_URLS.addParent(school.id)).
      const url =
        type === "student"
          ? API_URLS.addStudents(school.id)
          : isStaff
          ? API_URLS.createStaff(school.id)
          : undefined;
      const response = await axiosPrivate.post(url, formData, {
        headers: { "Content-Type": "application/json" },
      });

      const newId = response.data.id;
      handleClose();
      navigate(`details/${newId}`);
    } catch (error) {
      console.error("Request error:", error);
      setErrors((prev) => ({
        ...prev,
        server: error.response?.data?.message || t("form.genericError"),
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const loadingIcon = isLoading ? (
    <CircularProgress size={20} color="inherit" />
  ) : null;

  const fullName = existingStaff
    ? `${existingStaff.first_name ?? ""} ${existingStaff.last_name ?? ""}`.trim()
    : "";

  return (
    <Modal open={open} onClose={handleClose}>
      <Box className="modalStyle">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={4}
        >
          <Typography variant="h5" fontWeight="bold" color="secondary">
            {t("modal.titleInfo", { title: t(`admin_titles.${title}`) })}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <BiX />
          </IconButton>
        </Box>

        {/* ---------- STEP: email check (staff only) ---------- */}
        {isStaff && step === "email" && (
          <>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {t("staff_modal.emailStepHint")}
            </Typography>
            <Box mb={3}>
              <Typography
                variant="body1"
                component="label"
                display="block"
                color="secondary"
                gutterBottom
              >
                {t("staff_modal.fields.email")}{" "}
                <span className="text-danger">*</span>
              </Typography>
              <input
                type="email"
                value={email}
                className={`form-control ${errors.email ? "is-invalid" : ""}`}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrors((prev) => ({ ...prev, email: undefined }));
                }}
                onKeyDown={(e) =>
                  e.key === "Enter" && !isLoading && handleCheckEmail()
                }
                placeholder="example@mail.com"
                disabled={isLoading}
                autoFocus
              />
              {errors.email && (
                <Typography className="errorText">{errors.email}</Typography>
              )}
            </Box>

            {errors.server && (
              <Typography className="errorText" mb={2}>
                {errors.server}
              </Typography>
            )}

            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleCheckEmail}
                disabled={isLoading}
                startIcon={loadingIcon}
                className="submitButton"
              >
                {t("staff_modal.checkEmail")}
              </Button>
            </Box>
          </>
        )}

        {/* ---------- STEP: existing staff found ---------- */}
        {isStaff && step === "existing" && existingStaff && (
          <>
            <Typography variant="h6" color="secondary" mb={1}>
              {checkStatus === "found"
                ? t("staff_modal.existing.otherSchoolTitle")
                : t("staff_modal.existing.thisSchoolTitle")}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {checkStatus === "found"
                ? t("staff_modal.existing.otherSchoolDesc")
                : t("staff_modal.existing.thisSchoolDesc")}
            </Typography>

            <Box mb={3} p={2} sx={{ background: "#f5eef9", borderRadius: "8px" }}>
              <Typography fontWeight={600}>{fullName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {existingStaff.email}
              </Typography>
              {existingStaff.phone && (
                <Typography variant="body2" color="text.secondary">
                  {existingStaff.phone}
                </Typography>
              )}
              {existingStaff.job_title && (
                <Typography variant="body2" color="text.secondary">
                  {existingStaff.job_title}
                </Typography>
              )}
            </Box>

            {errors.server && (
              <Typography className="errorText" mb={2}>
                {errors.server}
              </Typography>
            )}

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Button
                onClick={() => {
                  setStep("email");
                  setExistingStaff(null);
                  setCheckStatus(null);
                  setErrors({});
                }}
                disabled={isLoading}
                sx={{ textTransform: "none", color: "#702E8F" }}
              >
                {t("staff_modal.checkAnother")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUseExistingStaff}
                disabled={isLoading}
                startIcon={loadingIcon}
                className="submitButton"
              >
                {checkStatus === "found"
                  ? t("staff_modal.existing.linkToSchool")
                  : t("staff_modal.existing.openDetails")}
              </Button>
            </Box>
          </>
        )}

        {/* ---------- STEP: create new (names) ---------- */}
        {step === "form" && (
          <>
            {/* حقول الاسم (FirstName & LastName) بجانب بعضهما */}
            <Box display="flex" gap={2} mb={3}>
              <Box flex={1}>
                <Typography
                  variant="body1"
                  component="label"
                  color="secondary"
                  gutterBottom
                >
                  {t("staff_modal.fields.firstName")}{" "}
                  <span className="text-danger">*</span>
                </Typography>
                <input
                  type="text"
                  value={firstName}
                  className={`form-control ${errors.firstName ? "is-invalid" : ""}`}
                  onChange={(e) => {
                    setFirstName(e.target.value);
                    setErrors((prev) => ({ ...prev, firstName: undefined }));
                  }}
                  placeholder={t("staff_modal.fields.firstName")}
                  disabled={isLoading}
                  autoFocus
                />
                {errors.firstName && (
                  <Typography className="errorText">{errors.firstName}</Typography>
                )}
              </Box>

              <Box flex={1}>
                <Typography
                  variant="body1"
                  component="label"
                  color="secondary"
                  gutterBottom
                >
                  {t("staff_modal.fields.lastName")}{" "}
                  <span className="text-danger">*</span>
                </Typography>
                <input
                  type="text"
                  value={lastName}
                  className={`form-control ${errors.lastName ? "is-invalid" : ""}`}
                  onChange={(e) => {
                    setLastName(e.target.value);
                    setErrors((prev) => ({ ...prev, lastName: undefined }));
                  }}
                  placeholder={t("staff_modal.fields.lastName")}
                  disabled={isLoading}
                />
                {errors.lastName && (
                  <Typography className="errorText">{errors.lastName}</Typography>
                )}
              </Box>
            </Box>

            {/* Staff: email was already entered in step 1, shown read-only */}
            {isStaff && (
              <Box mb={3}>
                <Typography
                  variant="body1"
                  component="label"
                  display="block"
                  color="secondary"
                  gutterBottom
                >
                  {t("staff_modal.fields.email")}{" "}
                  <span className="text-danger">*</span>
                </Typography>
                <input
                  type="email"
                  value={email}
                  className={`form-control ${errors.email ? "is-invalid" : ""}`}
                  readOnly
                  disabled
                />
                {errors.email && (
                  <Typography className="errorText">{errors.email}</Typography>
                )}
              </Box>
            )}

            {errors.server && (
              <Typography className="errorText" mb={2}>
                {errors.server}
              </Typography>
            )}

            <Box
              display="flex"
              justifyContent={isStaff ? "space-between" : "flex-end"}
              alignItems="center"
            >
              {isStaff && (
                <Button
                  onClick={() => {
                    setStep("email");
                    setErrors({});
                  }}
                  disabled={isLoading}
                  sx={{ textTransform: "none", color: "#702E8F" }}
                >
                  {t("staff_modal.changeEmail")}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={isLoading}
                startIcon={loadingIcon}
                className="submitButton"
              >
                {t("modal.addTitle", { title: t(`admin_titles.${title}`) })}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}